<template>
    <section class="w-full mb-20 lg:mb-0 p-5">
        <h2 class="text-center mb-4">
            <span v-if="subtitle" class="text-sm text-gray-600">{{ subtitle }}</span>
            <br v-if="subtitle" />
            {{ title }}
        </h2>
        <div class="px-4">
            <div class="mb-4 w-full">
                <!-- Items pro Kategorie -->
                <div class="grid lg:grid-cols-2 gap-8 justify-items-start px-4">
                    <div v-for="gruppe in gruppierteItems" :key="gruppe.category" class="mb-4 w-full">
                        <h3 class="text-xl mb-2 text-center">
                            {{ gruppe.category }}
                        </h3>

                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2">
                            <div
                                v-for="(item, idx) in gruppe.items"
                                :key="idx"
                                class="max-w-[300px] bg-blue text-white flex items-center py-3 px-3 lg:py-1 rounded-lg w-full text-center justify-between cursor-pointer"
                                @click="marketingModalOpen = item"
                            >
                                <div v-if="item.logoLoading" class="w-8 h-8 mr-2 bg-blue-300 rounded-full animate-pulse"></div>
                                <img v-else-if="item.logoUrl" :src="item.logoUrl" class="w-8 h-8 mr-2" alt="" />
                                <div v-else class=""></div>

                                <div class="flex flex-col items-start flex-1 min-w-0 text-left">
                                    <span>{{ item.title }}</span>
                                    <span v-if="item.status" class="mt-1 inline-flex rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide">
                                        {{ item.status }}
                                    </span>
                                </div>

                                <teleport to="body">
                                    <Popup type="text" :overflow-hidden="true" :open="marketingModalOpen === item" @open="closeAction()">
                                        <p v-if="item.status" class="pt-4 text-sm font-semibold uppercase tracking-wide text-blue-700">{{ item.status }}</p>
                                        <p class="pt-4">{{ localizedDescription(item) }}</p>
                                    </Popup>
                                </teleport>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { fetchData } from '../../fetchData.js';
import Popup from '../miscellaneous/Popup.vue';

export default {
    name: 'ProductListSection',
    components: {
        Popup
    },
    props: {
        title: {
            type: String,
            required: true
        },
        subtitle: {
            type: String,
            default: ''
        },
        items: {
            type: Array,
            required: true
        }
    },
    data: () => ({
        marketingModalOpen: false,
        enrichedItems: [] // Will hold items with logo URLs
    }),
    computed: {
        currentLang() {
            const region = window?.meta?.region_configuration?.region ?? 'de';

            return region === 'de' ? 'de' : 'en';
        },

        gruppierteItems() {
            const groups = {};
            (this.enrichedItems.length ? this.enrichedItems : this.items).forEach((item) => {
                const category = typeof item.category === 'object' ? (item.category[this.currentLang] ?? item.category.de) : item.category;

                if (!groups[category]) {
                    groups[category] = [];
                }

                groups[category].push(item);
            });

            return Object.entries(groups).map(([category, items]) => ({
                category,
                items
            }));
        }
    },
    watch: {
        // Watch for changes in the items prop
        items: {
            handler(newItems) {
                if (newItems && newItems.length) {
                    this.enrichItemsWithLogos(newItems);
                }
            },
            immediate: true, // Run immediately when component is created
            deep: true
        }
    },
    methods: {
        localizedDescription(item) {
            if (typeof item.description === 'object') {
                return item.description[this.currentLang] ?? item.description.de ?? '';
            }

            return item.description ?? '';
        },
        closeAction() {
            this.marketingModalOpen = false;
        },

        async enrichItemsWithLogos(originalItems) {
            // First, create base items with loading state
            this.enrichedItems = originalItems.map((item) => ({
                ...item,
                logoLoading: true,
                logoUrl: null
            }));

            // Then fetch logos for each item
            const fetchPromises = originalItems.map(async (item, index) => {
                try {
                    const logoUrl = await this.fetchLogo(item.symbol);

                    // Update the specific item while preserving others
                    const updatedItems = [...this.enrichedItems];
                    updatedItems[index] = {
                        ...updatedItems[index],
                        logoUrl,
                        logoLoading: false
                    };
                    this.enrichedItems = updatedItems;
                } catch (error) {
                    // Update with error state (no logo)
                    const updatedItems = [...this.enrichedItems];
                    updatedItems[index] = {
                        ...updatedItems[index],
                        logoLoading: false,
                        logoUrl: null
                    };
                    this.enrichedItems = updatedItems;
                }
            });

            // Wait for all logos to be fetched (optional - you can remove await if you want them to load asynchronously)
            await Promise.all(fetchPromises);
        },

        async fetchLogo(symboleName) {
            try {
                const response = await fetchData(`/api/v1/market-symbols-logo/${encodeURIComponent(symboleName)}`);

                if (response && response.result && response.result.url) {
                    return response.result.url;
                } else {
                    console.error('Unexpected response structure:', response);

                    return null;
                }
            } catch (error) {
                console.error('Error fetching logo:', error);

                return null;
            }
        }
    }
};
</script>
